import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { generateUUID } from "../utils/idempotency";
import {
  getCredits,
  getPackages,
  purchaseCredits,
  getHistory,
  reset,
} from "../features/credits/creditsSlice";
import { useToast } from "../components/Toast";
import {
  HiOutlineCreditCard,
  HiOutlineSparkles,
  HiOutlineLightningBolt,
  HiOutlineGift,
  HiOutlineClock,
  HiOutlineArrowUp,
  HiOutlineArrowDown,
  HiCheckCircle,
  HiOutlineShieldCheck,
  HiOutlineStar,
  HiStar,
} from "react-icons/hi";

function CreditStore() {
  const toast = useToast();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { user } = useSelector((state) => state.auth);
  const { packages, transactions, isLoading, isSuccess, isError, message } =
    useSelector((state) => state.credits);
  const balance = user?.data?.balance || 0;

  const [selectedPackage, setSelectedPackage] = useState(null);
  const [isPurchasing, setIsPurchasing] = useState(false);

  const email = user?.data?.email;

  useEffect(() => {
    if (!user) {
      navigate("/login");
      return;
    }

    dispatch(getCredits(email));
    dispatch(getPackages());
    dispatch(getHistory({ email, limit: 10 }));
  }, [user, email, dispatch, navigate]);

  useEffect(() => {
    if (isError) {
      toast.error(message);
    }
    if (isSuccess && isPurchasing) {
      toast.success("Credits purchased successfully!");
      setSelectedPackage(null);
      setIsPurchasing(false);
    }
    dispatch(reset());
  }, [isError, isSuccess, message, isPurchasing, dispatch, toast]);

  const handlePurchase = (pkg) => {
    setSelectedPackage(pkg);
  };

  const confirmPurchase = () => {
    if (!selectedPackage) return;

    const idempotencyKey = generateUUID();

    setIsPurchasing(true);
    dispatch(
      purchaseCredits({
        email,
        packageId: selectedPackage.id,
        paymentMethod: "card",
        idempotencyKey,
      })
    );
  };

  const getPackageIcon = (id) => {
    switch (id) {
      case "starter":
        return <HiOutlineSparkles className="text-2xl" />;
      case "popular":
        return <HiOutlineLightningBolt className="text-2xl" />;
      case "bestvalue":
        return <HiOutlineGift className="text-2xl" />;
      case "premium":
        return <HiOutlineStar className="text-2xl" />;
      default:
        return <HiOutlineCreditCard className="text-2xl" />;
    }
  };

  const getPackageColor = (id) => {
    switch (id) {
      case "starter":
        return "from-blue-500 to-blue-600";
      case "popular":
        return "from-purple-500 to-purple-600";
      case "bestvalue":
        return "from-emerald-500 to-emerald-600";
      case "premium":
        return "from-amber-500 to-amber-600";
      default:
        return "from-gray-500 to-gray-600";
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="min-h-screen bg-[#0b0e14] py-20 px-4 md:px-8 selection:bg-cyan-500/30 relative overflow-hidden">
      {/* Background Decorative Elements */}
      <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-purple-600/5 rounded-full blur-[160px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[800px] h-[800px] bg-cyan-500/5 rounded-full blur-[160px] pointer-events-none" />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row items-center justify-between mb-16 gap-8">
          <div className="space-y-4 text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-[10px] font-black text-cyan-400 uppercase tracking-widest">
              <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full animate-ping" />
              Loot Dispatch System v2.4
            </div>
            <h1 className="text-4xl md:text-7xl font-black text-white uppercase italic tracking-tighter">
              Gamer <span className="text-cyan-400">Loot</span> Store
            </h1>
            <p className="text-gray-500 font-bold uppercase tracking-[0.2em] text-xs max-w-lg">
              Acquire achievement pts to unlock high-performance hardware
              modules.
            </p>
          </div>

          <div className="bg-white/5 backdrop-blur-2xl p-8 rounded-[2.5rem] border border-white/10 shadow-2xl min-w-[320px] relative group overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-amber-500/0 via-amber-500 to-amber-500/0" />
            <p className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] mb-4 italic">
              Bounty Balance
            </p>
            <div className="flex items-baseline gap-3">
              <span className="text-5xl font-black text-white italic tracking-tighter">
                {balance.toLocaleString()}
              </span>
              <span className="text-amber-500 font-black text-lg uppercase tracking-widest italic animate-pulse">
                PTS
              </span>
            </div>
            <div className="mt-6 flex items-center gap-2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
              <HiOutlineShieldCheck className="text-green-500 text-sm" />
              Verified_Secured_Ledger
            </div>
          </div>
        </div>

        {/* Credit Packages Grid */}
        <div className="mb-24">
          <div className="flex items-center gap-4 mb-12">
            <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter">
              Available Modules
            </h2>
            <div className="flex-1 h-px bg-white/5" />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {packages.map((pkg) => (
              <div
                key={pkg.id}
                onClick={() => handlePurchase(pkg)}
                className={`relative group bg-white/5 backdrop-blur-xl rounded-[2.5rem] p-8 border transition-all duration-500 cursor-pointer overflow-hidden ${
                  selectedPackage?.id === pkg.id
                    ? "border-cyan-500 shadow-[0_0_40px_rgba(6,182,212,0.1)] -translate-y-2"
                    : "border-white/10 hover:border-cyan-500/30"
                }`}
              >
                {/* Glow Effect */}
                <div
                  className={`absolute -inset-1 opacity-0 group-hover:opacity-20 transition-opacity duration-500 bg-gradient-to-br ${getPackageColor(
                    pkg.id
                  )} blur-xl`}
                />

                {pkg.id === "bestvalue" && (
                  <div className="absolute top-5 right-5 flex items-center gap-1 px-3 py-1 bg-emerald-500 text-black text-[8px] font-black rounded-full uppercase tracking-widest animate-bounce">
                    Optimum_Rate
                  </div>
                )}

                <div
                  className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${getPackageColor(
                    pkg.id
                  )} flex items-center justify-center text-white mb-8 shadow-2xl shadow-indigo-500/20 relative z-10 font-black text-2xl italic`}
                >
                  {getPackageIcon(pkg.id)}
                </div>

                <div className="relative z-10">
                  <h3 className="text-xl font-black text-white uppercase italic tracking-tighter mb-2 group-hover:text-cyan-400 transition-colors">
                    {pkg.name}
                  </h3>
                  <div className="flex items-baseline gap-1 mb-8">
                    <span className="text-4xl font-black text-white italic tracking-tighter">
                      ${pkg.price}
                    </span>
                    <span className="text-gray-500 text-[10px] font-black uppercase tracking-widest">
                      USD_MATRIX
                    </span>
                  </div>

                  <div className="space-y-4 mb-10">
                    <div className="flex items-center gap-3">
                      <div className="w-5 h-5 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center justify-center">
                        <HiCheckCircle className="text-green-500 text-xs" />
                      </div>
                      <span className="text-xs font-black text-white uppercase italic">
                        {pkg.credits.toLocaleString()} Achievement Pts
                      </span>
                    </div>
                    {pkg.bonus > 0 && (
                      <div className="flex items-center gap-3">
                        <div className="w-5 h-5 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                          <HiStar className="text-cyan-400 text-xs animate-pulse" />
                        </div>
                        <span className="text-xs font-black text-cyan-400 uppercase italic">
                          +{pkg.bonus}% Bonus Loaded
                        </span>
                      </div>
                    )}
                  </div>

                  <button
                    className={`w-full py-4 rounded-2xl font-black uppercase tracking-[0.2em] italic text-[10px] transition-all duration-300 ${
                      selectedPackage?.id === pkg.id
                        ? "bg-white text-black shadow-xl"
                        : "bg-white/5 text-gray-400 border border-white/10 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    {selectedPackage?.id === pkg.id
                      ? "MODULE_LOCKED"
                      : "INITIATE_SYNC"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Confirmation Overlay */}
        {selectedPackage && (
          <div className="mb-24 animate-slideUp">
            <div className="bg-gradient-to-r from-cyan-600 to-purple-700 p-[1px] rounded-[3rem] shadow-2xl">
              <div className="bg-[#0b0e14] rounded-[3rem] p-10 flex flex-col md:flex-row items-center justify-between gap-10">
                <div className="flex items-center gap-8">
                  <div
                    className={`w-20 h-20 rounded-3xl bg-gradient-to-br ${getPackageColor(
                      selectedPackage.id
                    )} flex items-center justify-center text-3xl text-white shadow-2xl`}
                  >
                    {getPackageIcon(selectedPackage.id)}
                  </div>
                  <div>
                    <p className="text-[10px] font-black text-gray-500 uppercase tracking-[0.3em] mb-2 italic">
                      Confirm Acquisition Protocol
                    </p>
                    <h3 className="text-3xl font-black text-white uppercase italic tracking-tighter">
                      {selectedPackage.name} Module
                    </h3>
                    <p className="text-cyan-400 font-bold uppercase tracking-widest text-xs mt-1">
                      Target: {selectedPackage.credits.toLocaleString()} Pts //
                      Extraction: ${selectedPackage.price}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-6 w-full md:w-auto">
                  <button
                    onClick={() => setSelectedPackage(null)}
                    className="px-8 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl font-black uppercase tracking-[0.2em] italic text-[10px] text-gray-400 hover:text-white transition-all"
                  >
                    ABORT_SYNC
                  </button>
                  <button
                    onClick={confirmPurchase}
                    disabled={isLoading}
                    className="flex-1 md:flex-none px-12 py-4 bg-white text-black hover:invert rounded-2xl font-black uppercase tracking-[0.2em] italic text-[10px] transition-all duration-500 shadow-xl shadow-cyan-500/20 disabled:opacity-50 flex items-center justify-center gap-3"
                  >
                    {isLoading ? (
                      <span className="animate-pulse">EXTRACTING...</span>
                    ) : (
                      <>
                        <HiOutlineCreditCard className="text-lg" />{" "}
                        AUTHORIZE_PAYMENT
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Transaction History Section */}
        <div>
          <div className="flex items-center gap-4 mb-12">
            <h2 className="text-2xl font-black text-white uppercase italic tracking-tighter">
              Transaction Log
            </h2>
            <div className="flex-1 h-px bg-white/5" />
          </div>

          {transactions.length === 0 ? (
            <div className="bg-white/5 backdrop-blur-xl rounded-[3rem] p-20 text-center border border-white/10">
              <div className="w-20 h-20 bg-white/5 rounded-3xl flex items-center justify-center mx-auto mb-8 border border-white/5">
                <HiOutlineClock className="text-4xl text-gray-700" />
              </div>
              <p className="text-gray-500 font-black uppercase tracking-[0.3em] italic">
                No active data logs
              </p>
              <p className="text-gray-700 text-xs font-bold uppercase tracking-widest mt-2">
                Initialize acquisition to populate terminal
              </p>
            </div>
          ) : (
            <div className="bg-white/5 backdrop-blur-xl rounded-[3rem] border border-white/10 overflow-hidden shadow-2xl">
              <div className="divide-y divide-white/5">
                {transactions.map((tx, idx) => (
                  <div
                    key={tx.id || idx}
                    className="p-8 flex items-center justify-between hover:bg-white/[0.03] transition-all group"
                  >
                    <div className="flex items-center gap-6">
                      <div
                        className={`w-14 h-14 rounded-2xl flex items-center justify-center shadow-lg transition-transform group-hover:scale-110 ${
                          tx.type === "credit"
                            ? "bg-green-500/10 border border-green-500/20 text-green-400"
                            : "bg-red-500/10 border border-red-500/20 text-red-500"
                        }`}
                      >
                        {tx.type === "credit" ? (
                          <HiOutlineArrowUp className="text-2xl" />
                        ) : (
                          <HiOutlineArrowDown className="text-2xl" />
                        )}
                      </div>
                      <div>
                        <p className="font-black text-white uppercase italic tracking-tighter text-lg leading-none mb-2">
                          {tx.description.replace(/Credits/g, "Pts")}
                        </p>
                        <div className="flex items-center gap-3 text-[10px] font-bold text-gray-500 uppercase tracking-widest">
                          <HiOutlineClock />
                          {formatDate(tx.createdAt)}
                          <span className="w-1 h-1 bg-gray-700 rounded-full" />
                          ID: {tx.id?.slice(-8).toUpperCase() || "INTERNAL"}
                        </div>
                      </div>
                    </div>
                    <div
                      className={`text-2xl font-black italic tracking-tighter ${
                        tx.type === "credit" ? "text-green-400" : "text-red-500"
                      }`}
                    >
                      {tx.type === "credit" ? "+" : "-"}
                      {tx.amount.toLocaleString()}
                      <span className="text-[10px] ml-1 uppercase tracking-widest not-italic opacity-50">
                        PTS
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default CreditStore;
